/**
 * @param {import('alpinejs').Alpine} Alpine
 */
export default Alpine => {
  /**
   * x-marquee directive
   *
   * Applies flexbox + CSS animations to create a marquee effect.
   *
   * Options:
   * - direction:      How the marquee should move: Right to left ("rtl", default) or left to right ("ltr")
   * - pauseOnHover:   Whether the marquee animation should pause on hovering with the mouse
   * - speed:          The speed of the marquee animation in pixels per second
   *                   This can also be set via CSS with the --marquee-speed custom property
   * - gutter:         The space between the marquee items
   *                   This can also be set via CSS with the --marquee-gutter custom property
   * - alignment:      The flex alignment of the marquee items (i.e. the align-items property)
   *                   This can also be set via CSS with the --marquee-alignment custom property
   */

  /**
   * @type {HTMLStyleElement|undefined}
   */
  let styleBlock

  let marqueeCounter = 0
  Alpine.directive('marquee', (el, { expression }, { evaluateLater, effect, cleanup }) => {
    marqueeCounter++

    // Evaluate options
    const getOptions = evaluateLater(expression || '{}')
    effect(() => {
      getOptions(({ direction, pauseOnHover, speed, gutter, alignment }) => {
        if (direction == null) {
          delete el.dataset.xDirection
        } else {
          el.dataset.xDirection = direction
        }

        if (pauseOnHover == null) {
          delete el.dataset.xPauseOnHover
        } else {
          el.dataset.xPauseOnHover = pauseOnHover
        }

        if (typeof speed === 'number' && speed > 0 && Number.isFinite(speed)) {
          el.style.setProperty('--marquee-speed', speed)
        } else {
          el.style.removeProperty('--marquee-speed')
        }

        if (gutter != null) {
          el.style.setProperty(
            '--marquee-gutter',
            typeof gutter === 'number' ? `${gutter}px` : gutter,
          )
        } else {
          el.style.removeProperty('--marquee-gutter')
        }

        if (alignment != null) {
          el.style.setProperty('--marquee-alignment', alignment)
        } else {
          el.style.removeProperty('--marquee-alignment')
        }
      })
    })

    if (!styleBlock) {
      // Inject styles
      styleBlock = document.createElement('style')
      styleBlock.innerHTML = `
      [x-marquee] {
        overflow: hidden;
        ---marquee-initial-position: 0px;
        ---marquee-target-position: calc(-1px * var(--marquee-items-wrapper-width));
      }

      [x-marquee][data-x-direction='ltr'] {
        ---marquee-initial-position: calc(-1px * var(--marquee-items-wrapper-width, 0));
        ---marquee-target-position: 0px;
      }

      @media (hover: hover) and (pointer: fine) {
        [x-marquee]:not([data-x-pause-on-hover="false"]):hover > .marquee-grid {
          animation-play-state: paused;
        }
      }

      [x-marquee] > .marquee-grid {
        display: flex;
        align-items: var(--marquee-alignment, center);
        max-width: 100%;
        animation-name: x-marquee;
        animation-timing-function: linear;
        animation-iteration-count: infinite;
        animation-duration: calc(var(--marquee-items-wrapper-width, 0) / var(--marquee-speed, 250) * 1s);
      }

      [x-marquee] > .marquee-grid > .marquee-items-wrapper {
        display: flex;
        gap: var(--marquee-gutter, 5vw);
        align-items: var(--marquee-alignment, center);
        padding-right: var(--marquee-gutter, 5vw);
      }

      @media (prefers-reduced-motion: reduce) {
        [x-marquee] > .marquee-grid {
          animation: none;
          display: block;
        }

        [x-marquee] > .marquee-grid > .marquee-items-wrapper {
          flex-wrap: wrap;
          padding-right: 0;
        }

        [x-marquee] > .marquee-grid > .marquee-items-wrapper:not(:first-child) {
          display: none;
        }
      }

      [x-marquee] > .marquee-grid > *,
      [x-marquee] > .marquee-grid > .marquee-items-wrapper > * {
        flex-shrink: 0;
      }

      @keyframes x-marquee {
        0% { transform: translateX(var(---marquee-initial-position)); }
        100% { transform: translateX(var(---marquee-target-position)); }
      }
      `
      document.head.appendChild(styleBlock)
    }

    // The items wrapper is a flex container constructed around the original children
    const itemsWrapper = document.createElement('div')
    itemsWrapper.className = 'marquee-items-wrapper'

    // The grid is a flex container that contains the items wrapper and one or more copies of it
    const grid = document.createElement('div')
    grid.className = 'marquee-grid'

    const children = [...el.children]
    for (let child of children) {
      itemsWrapper.appendChild(child)
    }
    grid.appendChild(itemsWrapper)
    el.appendChild(grid)

    const mediaReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')

    function updateMarqueeDom() {
      // Bail if reduced motion is enabled
      if (mediaReducedMotion.matches) return
      if (itemsWrapperWidth === 0) return
      if (containerWidth === 0) return

      el.style.setProperty('--marquee-items-wrapper-width', itemsWrapperWidth)

      const targetGridWidth = containerWidth * 2
      const times = Math.max(Math.ceil(targetGridWidth / itemsWrapperWidth), 2)

      while (grid.children.length !== times) {
        if (grid.children.length > times) {
          grid.removeChild(grid.lastElementChild)
        } else {
          const clone = itemsWrapper.cloneNode(true)
          clone.setAttribute('aria-hidden', 'true')
          grid.appendChild(clone)
        }
      }

      // For some reason, Safari does not start the animation without re-applying it first
      setTimeout(() => {
        grid.style.animation = 'none'
        setTimeout(() => {
          grid.style.removeProperty('animation')
        })
      }, 0)
    }

    let containerWidth = el.getBoundingClientRect().width
    const containerSizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        containerWidth = entry.borderBoxSize[0].inlineSize
        updateMarqueeDom()
      }
    })
    containerSizeObserver.observe(el)

    let itemsWrapperWidth = itemsWrapper.getBoundingClientRect().width
    const itemsWrapperSizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        itemsWrapperWidth = entry.borderBoxSize[0].inlineSize
        updateMarqueeDom()
      }
    })
    itemsWrapperSizeObserver.observe(itemsWrapper)

    updateMarqueeDom()

    cleanup(() => {
      marqueeCounter--

      // Undo attribute changes
      delete el.dataset.xDirection
      delete el.dataset.xPauseOnHover
      el.style.removeProperty('--marquee-speed')
      el.style.removeProperty('--marquee-gutter')
      el.style.removeProperty('--marquee-alignment')

      // Unobserve elements
      containerSizeObserver.disconnect()
      itemsWrapperSizeObserver.disconnect()

      // Restore original children
      for (const child of children) {
        el.appendChild(child)
      }
      grid.remove()

      // Remove global styles if no marquee left
      if (marqueeCounter === 0) {
        styleBlock.remove()
        styleBlock = undefined
      }
    })
  })
}
